import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, Instagram, Twitter, Star, ArrowRight, X, 
  ChevronLeft, ChevronRight, Images, Search, Filter, Globe, 
  Calendar, Users, ChevronDown, Flag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to resolve image URLs - /uploads/* paths are served from backend
const resolveImageUrl = (imagePath) => {
  if (!imagePath) return null;
  // If it's already a full URL (http/https), return as is
  if (imagePath.startsWith('http')) return imagePath;
  // If it's an uploads path, prepend the API URL
  if (imagePath.startsWith('/uploads/')) {
    return `${API_URL}/api${imagePath}`;
  }
  // Otherwise return as is (for legacy local paths)
  return imagePath;
};

// Array of placeholder images for players without photos - variety of football player silhouettes and action shots
const PLACEHOLDER_IMAGES = [
  "https://images.unsplash.com/photo-1634152557768-b5bb22302a56?w=400&h=400&fit=crop&crop=top", // Player silhouette heroic
  "https://images.unsplash.com/photo-1695893155157-34bd2e939dac?w=400&h=400&fit=crop&crop=center", // Silhouette dark
  "https://images.unsplash.com/photo-1649023234155-6383f504949e?w=400&h=400&fit=crop&crop=center", // Silhouette green
  "https://images.unsplash.com/photo-1761225092045-698d1c4a9f43?w=400&h=400&fit=crop&crop=center", // Teenager soccer tricks
  "https://images.unsplash.com/photo-1760420919593-c1ae7509faaf?w=400&h=400&fit=crop&crop=center", // Young player field
  "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=400&h=400&fit=crop&crop=center", // Soccer action
  "https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=400&h=400&fit=crop&crop=top", // Player with ball
  "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400&h=400&fit=crop&crop=center", // Soccer match
];

// Function to get a consistent placeholder image based on player name (so same player always gets same image)
const getPlaceholderImage = (playerName) => {
  const hash = playerName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return PLACEHOLDER_IMAGES[hash % PLACEHOLDER_IMAGES.length];
};

// Player Preview Modal
const PlayerPreviewModal = ({ player, isOpen, onClose }) => {
  const [activeImage, setActiveImage] = useState(0);
  
  if (!isOpen || !player) return null;
  
  // Helper to check if image is valid
  const isValidImage = (img) => {
    if (!img) return false;
    return true; // All images from DB are now valid (either /uploads/ or https://)
  };
  
  const getAllImages = () => {
    const imgs = [];
    
    // Check main image
    if (player.image && isValidImage(player.image)) {
      imgs.push(resolveImageUrl(player.image));
    }
    
    // Check secondary image
    if (player.image2 && isValidImage(player.image2)) {
      imgs.push(resolveImageUrl(player.image2));
    }
    
    // Check gallery
    if (player.gallery && player.gallery.length > 0) {
      player.gallery.forEach(img => {
        if (isValidImage(img)) imgs.push(resolveImageUrl(img));
      });
    }
    
    // If no valid images, use placeholder based on player name
    if (imgs.length === 0) {
      imgs.push(getPlaceholderImage(player.name));
    }
    
    return imgs;
  };
  
  const images = getAllImages();
  
  const nextImage = () => setActiveImage((prev) => (prev + 1) % images.length);
  const prevImage = () => setActiveImage((prev) => (prev - 1 + images.length) % images.length);

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="relative w-full sm:max-w-4xl bg-neutral-900 sm:rounded-2xl overflow-hidden min-h-screen sm:min-h-0 my-0 sm:my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
        >
          <X size={20} />
        </button>
        
        <div className="flex flex-col lg:flex-row">
          {/* Image Gallery */}
          <div className="relative lg:w-1/2 aspect-[4/3] sm:aspect-square bg-black flex-shrink-0">
            {images.length > 0 ? (
              <>
                <img
                  src={images[activeImage]}
                  alt={player.name}
                  className="w-full h-full object-cover object-top"
                />
                {images.length > 1 && (
                  <>
                    <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white">
                      <ChevronLeft size={20} />
                    </button>
                    <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white">
                      <ChevronRight size={20} />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-2 py-1 bg-black/50 rounded-full text-white text-xs">
                      {activeImage + 1} / {images.length}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="w-full h-full bg-neutral-800 flex items-center justify-center">
                <span className="text-white/30">Sin imagen</span>
              </div>
            )}
          </div>
          
          {/* Player Info */}
          <div className="lg:w-1/2 p-5 sm:p-6 pb-8">
            <div className="flex flex-wrap gap-2 mb-3">
              {player.is_featured && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-500 text-xs font-medium rounded-full">
                  <Star size={10} fill="currentColor" />
                  Destacado
                </span>
              )}
              {player.national_team_history && player.national_team_history.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-sky-500/20 text-sky-400 text-xs font-medium rounded-full">
                  🇺🇾 Selección
                </span>
              )}
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">{player.name}</h2>
            <div className="flex items-center gap-2 text-white/50 text-sm mb-4">
              <MapPin size={14} />
              <span>{player.club || 'Sin equipo'}</span>
            </div>
            
            <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
              <div className="bg-white/5 rounded-lg p-2.5 sm:p-3">
                <span className="text-white/40 text-[10px] sm:text-xs">Posición</span>
                <p className="text-white text-xs sm:text-sm font-medium">{player.position}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-2.5 sm:p-3">
                <span className="text-white/40 text-[10px] sm:text-xs">País</span>
                <p className="text-white text-xs sm:text-sm font-medium">{player.country}</p>
              </div>
              {player.age && (
                <div className="bg-white/5 rounded-lg p-2.5 sm:p-3">
                  <span className="text-white/40 text-[10px] sm:text-xs">Edad</span>
                  <p className="text-white text-xs sm:text-sm font-medium">{player.age} años</p>
                </div>
              )}
              {player.category && (
                <div className="bg-white/5 rounded-lg p-2.5 sm:p-3">
                  <span className="text-white/40 text-[10px] sm:text-xs">Categoría</span>
                  <p className="text-white text-xs sm:text-sm font-medium">{player.category}</p>
                </div>
              )}
            </div>
            
            {/* National Team Summary - Más prominente */}
            {player.national_team_history && player.national_team_history.length > 0 ? (
              <div className="bg-gradient-to-r from-sky-500/20 via-blue-500/15 to-sky-500/20 border border-sky-500/30 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-lg shadow-lg">
                      🇺🇾
                    </div>
                    <div>
                      <span className="text-sky-300 text-sm font-semibold block">Selección Uruguay</span>
                      <span className="text-white/50 text-[10px]">{player.national_team_history.length} convocatoria{player.national_team_history.length > 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {player.national_team_history.map((entry, idx) => (
                    <div key={idx} className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium ${
                      entry.team_type === 'Mayor' 
                        ? 'bg-sky-500/30 text-sky-200 border border-sky-400/30' 
                        : 'bg-white/10 text-white/70 border border-white/10'
                    }`}>
                      <span className="font-semibold">{entry.team_type}</span>
                      {entry.year && <span className="text-white/50 ml-1">'{entry.year.slice(-2)}</span>}
                      {entry.is_friendly && <span className="ml-1 text-orange-400">(A)</span>}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 text-white/30">
                  <span className="text-lg opacity-50">🇺🇾</span>
                  <span className="text-xs">Sin convocatorias a selección registradas</span>
                </div>
              </div>
            )}
            
            {player.biography && (
              <p className="text-white/60 text-xs sm:text-sm leading-relaxed line-clamp-3 mb-4">
                {player.biography}
              </p>
            )}
            
            {(player.instagram || player.twitter) && (
              <div className="flex gap-2 mb-4">
                {player.instagram && (
                  <a href={player.instagram} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white hover:scale-110 transition-transform">
                    <Instagram size={16} />
                  </a>
                )}
                {player.twitter && (
                  <a href={player.twitter} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 bg-sky-500 rounded-lg flex items-center justify-center text-white hover:scale-110 transition-transform">
                    <Twitter size={16} />
                  </a>
                )}
              </div>
            )}
            
            {player.has_page && player.slug && (
              <Link
                to={`/jugadores/${player.slug}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-black rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
              >
                Ver perfil completo
                <ArrowRight size={16} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Large Player Card (for featured players)
const FeaturedPlayerCard = ({ player, onPreviewClick }) => {
  const hasMultipleImages = player.image2 || (player.gallery && player.gallery.length > 0);
  const totalImages = 1 + (player.image2 ? 1 : 0) + (player.gallery?.length || 0);
  const hasNationalTeam = player.national_team_history && player.national_team_history.length > 0;
  
  const getPlayerImage = () => {
    if (player.image) return resolveImageUrl(player.image);
    return getPlaceholderImage(player.name);
  };
  
  const getSecondaryImage = () => {
    if (player.image2) return resolveImageUrl(player.image2);
    return null;
  };
  
  return (
    <div
      className="group relative bg-white/5 rounded-2xl overflow-hidden border border-white/10 hover:border-white/20 transition-all duration-500 cursor-pointer"
      onClick={() => onPreviewClick(player)}
    >
      {/* Badges - Left side */}
      <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
        {player.is_featured && (
          <div className="px-2 py-1 bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-[10px] font-medium rounded-full flex items-center gap-1">
            <Star size={10} fill="currentColor" />
            Destacado
          </div>
        )}
        {hasNationalTeam && (
          <div className="px-2 py-1 bg-gradient-to-r from-sky-500 to-blue-500 text-white text-[10px] font-medium rounded-full flex items-center gap-1">
            🇺🇾
            <span>Selección</span>
          </div>
        )}
      </div>
      
      {hasMultipleImages && (
        <div className="absolute top-3 right-3 z-10 px-2 py-1 bg-black/50 backdrop-blur-sm text-white text-[10px] rounded-full flex items-center gap-1">
          <Images size={10} />
          {totalImages}
        </div>
      )}

      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={getPlayerImage()}
          alt={player.name}
          className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-500 ${
            player.image2 ? 'group-hover:opacity-0' : 'group-hover:scale-105'
          }`}
        />
        {player.image2 && (
          <img
            src={getSecondaryImage()}
            alt={`${player.name} - 2`}
            className="absolute inset-0 w-full h-full object-cover object-top opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-105"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        
        <div className="absolute bottom-16 left-0 right-0 flex justify-center opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
          <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs flex items-center gap-1">
            Ver más <ArrowRight size={12} />
          </span>
        </div>
      </div>

      <div className="p-4 -mt-14 relative">
        <div className="flex items-center gap-1.5 text-white/40 text-xs font-light mb-1">
          <MapPin size={12} />
          <span>{player.club || 'Sin equipo'}</span>
        </div>
        <h3 className="text-lg font-light text-white mb-1">{player.name}</h3>
        <div className="flex items-center gap-2 text-white/50 text-xs font-light">
          <span>{player.position}</span>
          <span className="w-1 h-1 bg-white/30 rounded-full" />
          <span>{player.age ? `${player.age} años` : player.category}</span>
        </div>
      </div>
    </div>
  );
};

// Small Player Card (for category players - more compact, square-ish)
const SmallPlayerCard = ({ player, onPreviewClick }) => {
  const hasNationalTeam = player.national_team_history && player.national_team_history.length > 0;
  
  const getPlayerImage = () => {
    if (player.image) return resolveImageUrl(player.image);
    return getPlaceholderImage(player.name);
  };
  
  const getSecondaryImage = () => {
    if (player.image2) return resolveImageUrl(player.image2);
    return null;
  };
  
  return (
    <div
      className="group relative bg-white/5 rounded-xl overflow-hidden border border-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer"
      onClick={() => onPreviewClick(player)}
    >
      {/* National team badge */}
      {hasNationalTeam && (
        <div className="absolute top-2 left-2 z-10 px-1.5 py-0.5 bg-gradient-to-r from-sky-500 to-blue-500 text-white text-[8px] font-medium rounded-full flex items-center gap-0.5">
          🇺🇾
        </div>
      )}
      
      <div className="relative aspect-square overflow-hidden">
        <img
          src={getPlayerImage()}
          alt={player.name}
          className={`w-full h-full object-cover object-top transition-all duration-500 ${
            player.image2 ? 'group-hover:opacity-0' : 'group-hover:scale-105'
          }`}
        />
        {player.image2 && (
          <img
            src={getSecondaryImage()}
            alt={`${player.name} - 2`}
            className="absolute inset-0 w-full h-full object-cover object-top opacity-0 group-hover:opacity-100 transition-all duration-500"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        
        {/* Quick info overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h3 className="text-sm font-medium text-white truncate">{player.name}</h3>
          <div className="flex items-center gap-1 text-white/60 text-[10px]">
            <span>{player.position}</span>
            {player.club && (
              <>
                <span>•</span>
                <span className="truncate">{player.club}</span>
              </>
            )}
          </div>
        </div>
        
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs">
            Ver ficha
          </span>
        </div>
      </div>
    </div>
  );
};

// Filter Dropdown Component
const FilterDropdown = ({ label, icon: Icon, options, value, onChange, placeholder, useObjectOptions = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Get display value for the button
  const getDisplayValue = () => {
    if (!value) return placeholder;
    if (useObjectOptions) {
      const found = options.find(opt => opt.value === value);
      return found ? found.label : value;
    }
    return value;
  };
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-sm ${
          value ? 'bg-white/10 border-white/30 text-white' : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20'
        }`}
      >
        <Icon size={14} />
        <span className="hidden sm:inline">{getDisplayValue()}</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-48 max-h-60 overflow-y-auto bg-neutral-900 border border-white/10 rounded-lg shadow-xl z-50">
            <button
              onClick={() => { onChange(''); setIsOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-white/50 hover:bg-white/5 transition-colors"
            >
              Todos
            </button>
            {options.map((option) => {
              const optValue = useObjectOptions ? option.value : option;
              const optLabel = useObjectOptions ? option.label : option;
              return (
                <button
                  key={optValue}
                  onClick={() => { onChange(optValue); setIsOpen(false); }}
                  className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                    value === optValue ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/5'
                  }`}
                >
                  {optLabel}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const AllPlayersPage = () => {
  const [allPlayers, setAllPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewPlayer, setPreviewPlayer] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterClub, setFilterClub] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterAbroad, setFilterAbroad] = useState('');
  const [filterNationalTeam, setFilterNationalTeam] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Check if player has played abroad based on club_history
  const hasPlayedAbroad = (player) => {
    if (player.club_history && player.club_history.length > 0) {
      return player.club_history.some(club => club.is_abroad === true);
    }
    return false;
  };

  // Check if player has national team experience
  const hasNationalTeamExp = (player) => {
    return player.national_team_history && player.national_team_history.length > 0;
  };

  // Check if player has specific national team category
  const hasNationalTeamCategory = (player, category) => {
    if (!player.national_team_history) return false;
    if (category === 'any') return player.national_team_history.length > 0;
    return player.national_team_history.some(entry => entry.team_type === category);
  };

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await fetch(`${API_URL}/api/players`);
        if (response.ok) {
          const data = await response.json();
          setAllPlayers(data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPlayers();
  }, []);

  // Get unique values for filters
  const clubs = useMemo(() => {
    const clubSet = new Set(allPlayers.map(p => p.club).filter(Boolean));
    return Array.from(clubSet).sort();
  }, [allPlayers]);

  const categories = useMemo(() => {
    const catSet = new Set(allPlayers.map(p => p.category).filter(Boolean));
    return Array.from(catSet).sort((a, b) => parseInt(b) - parseInt(a));
  }, [allPlayers]);

  // Filter players
  const filteredPlayers = useMemo(() => {
    return allPlayers.filter(player => {
      // Search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          player.name?.toLowerCase().includes(query) ||
          player.club?.toLowerCase().includes(query) ||
          player.position?.toLowerCase().includes(query) ||
          player.country?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Club filter
      if (filterClub && player.club !== filterClub) return false;
      
      // Category filter
      if (filterCategory && player.category !== filterCategory) return false;
      
      // Abroad filter - based on club_history is_abroad field
      if (filterAbroad) {
        const playedAbroad = hasPlayedAbroad(player);
        if (filterAbroad === 'Extranjero' && !playedAbroad) return false;
        if (filterAbroad === 'Uruguay' && playedAbroad) return false;
      }
      
      // National team filter
      if (filterNationalTeam) {
        if (!hasNationalTeamCategory(player, filterNationalTeam)) return false;
      }
      
      return true;
    });
  }, [allPlayers, searchQuery, filterClub, filterCategory, filterAbroad, filterNationalTeam]);

  // Split into featured and by category
  const featuredPlayers = useMemo(() => {
    return filteredPlayers.filter(p => p.is_featured).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  }, [filteredPlayers]);

  const playersByCategory = useMemo(() => {
    const nonFeatured = filteredPlayers.filter(p => !p.is_featured);
    const grouped = {};
    
    nonFeatured.forEach(player => {
      const cat = player.category || 'Sin categoría';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(player);
    });
    
    // Sort categories descending
    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      if (a === 'Sin categoría') return 1;
      if (b === 'Sin categoría') return -1;
      return parseInt(b) - parseInt(a);
    });
    
    return { grouped, sortedCategories };
  }, [filteredPlayers]);

  const hasActiveFilters = searchQuery || filterClub || filterCategory || filterAbroad || filterNationalTeam;

  const clearFilters = () => {
    setSearchQuery('');
    setFilterClub('');
    setFilterCategory('');
    setFilterAbroad('');
    setFilterNationalTeam('');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-black/95 backdrop-blur-xl py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-white hover:text-white/80 transition-colors">
            <ArrowLeft size={20} />
            <span className="font-light hidden sm:inline">Volver al inicio</span>
          </Link>
          <Link to="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_faro-player-showcase/artifacts/6o32bteg_Logo_Blanco.png"
              alt="FARO Sports"
              className="h-8 sm:h-10 w-auto"
            />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Title */}
          <div className="mb-8">
            <span className="inline-block text-white/50 text-xs font-light tracking-[0.3em] uppercase mb-3">
              Nuestro Roster Completo
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white">
              Todos los{' '}
              <span className="text-white/50 font-extralight">Jugadores</span>
            </h1>
          </div>

          {/* Search and Filters */}
          <div className="mb-8 space-y-4">
            {/* Search Bar */}
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por nombre, equipo, posición..."
                  className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:border-white/30 outline-none transition-colors"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-colors ${
                  showFilters || hasActiveFilters 
                    ? 'bg-white/10 border-white/30 text-white' 
                    : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20'
                }`}
              >
                <Filter size={18} />
                <span className="hidden sm:inline">Filtros</span>
                {hasActiveFilters && (
                  <span className="w-2 h-2 bg-yellow-500 rounded-full" />
                )}
              </button>
            </div>
            
            {/* Quick Filter: Selección Nacional */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterNationalTeam(filterNationalTeam === 'any' ? '' : 'any')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-300 text-sm font-medium ${
                  filterNationalTeam === 'any'
                    ? 'bg-gradient-to-r from-sky-500 to-blue-500 border-sky-400 text-white shadow-lg shadow-sky-500/30'
                    : 'bg-white/5 border-white/20 text-white/70 hover:border-sky-400/50 hover:text-sky-300'
                }`}
              >
                <span className="text-base">🇺🇾</span>
                <span>Con Selección</span>
                {filterNationalTeam === 'any' && (
                  <X size={14} className="ml-1" />
                )}
              </button>
              
              <button
                onClick={() => setFilterAbroad(filterAbroad === 'Extranjero' ? '' : 'Extranjero')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-300 text-sm font-medium ${
                  filterAbroad === 'Extranjero'
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 border-emerald-400 text-white shadow-lg shadow-emerald-500/30'
                    : 'bg-white/5 border-white/20 text-white/70 hover:border-emerald-400/50 hover:text-emerald-300'
                }`}
              >
                <Globe size={14} />
                <span>Jugó en Extranjero</span>
                {filterAbroad === 'Extranjero' && (
                  <X size={14} className="ml-1" />
                )}
              </button>
            </div>

            {/* Filter Options */}
            {showFilters && (
              <div className="flex flex-wrap gap-3 p-4 bg-white/5 rounded-xl border border-white/10">
                <FilterDropdown
                  label="Equipo"
                  icon={Users}
                  options={clubs}
                  value={filterClub}
                  onChange={setFilterClub}
                  placeholder="Equipo"
                />
                <FilterDropdown
                  label="Categoría"
                  icon={Calendar}
                  options={categories}
                  value={filterCategory}
                  onChange={setFilterCategory}
                  placeholder="Categoría"
                />
                <FilterDropdown
                  label="Ubicación"
                  icon={Globe}
                  options={['Extranjero', 'Uruguay']}
                  value={filterAbroad}
                  onChange={setFilterAbroad}
                  placeholder="Ubicación"
                />
                <FilterDropdown
                  label="Selección"
                  icon={Flag}
                  options={[
                    { value: 'any', label: 'Con experiencia' },
                    { value: 'Mayor', label: 'Mayor (Primera)' },
                    { value: 'Sub-23', label: 'Sub-23' },
                    { value: 'Sub-20', label: 'Sub-20' },
                    { value: 'Sub-17', label: 'Sub-17' },
                    { value: 'Sub-15', label: 'Sub-15' },
                  ]}
                  value={filterNationalTeam}
                  onChange={setFilterNationalTeam}
                  placeholder="Selección"
                  useObjectOptions
                />
                
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-white/60 hover:text-white transition-colors"
                  >
                    <X size={14} />
                    Limpiar filtros
                  </button>
                )}
              </div>
            )}

            {/* Results count */}
            {hasActiveFilters && (
              <p className="text-white/50 text-sm">
                {filteredPlayers.length} jugador{filteredPlayers.length !== 1 ? 'es' : ''} encontrado{filteredPlayers.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          )}

          {/* Featured Players Section */}
          {!loading && featuredPlayers.length > 0 && (
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <Star size={20} className="text-yellow-500 fill-yellow-500" />
                <h2 className="text-xl sm:text-2xl font-light text-white">
                  Jugadores Destacados
                </h2>
                <span className="text-white/40 text-sm">({featuredPlayers.length})</span>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
                {featuredPlayers.map((player) => (
                  <FeaturedPlayerCard 
                    key={player.id} 
                    player={player} 
                    onPreviewClick={setPreviewPlayer}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Players by Category */}
          {!loading && playersByCategory.sortedCategories.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-xl sm:text-2xl font-light text-white">
                  Por Categoría
                </h2>
              </div>
              
              {playersByCategory.sortedCategories.map((category) => (
                <div key={category} className="mb-10">
                  {/* Category Header */}
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-2xl sm:text-3xl font-bold text-white/20">
                      {category}
                    </span>
                    <div className="flex-1 h-px bg-gradient-to-r from-white/20 to-transparent" />
                    <span className="text-white/40 text-xs">
                      {playersByCategory.grouped[category]?.length || 0} jugadores
                    </span>
                  </div>
                  
                  {/* Small Cards Grid - More columns, smaller cards */}
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-3">
                    {playersByCategory.grouped[category]?.map((player) => (
                      <SmallPlayerCard 
                        key={player.id} 
                        player={player}
                        onPreviewClick={setPreviewPlayer}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </section>
          )}

          {/* Empty State */}
          {!loading && filteredPlayers.length === 0 && (
            <div className="text-center py-20">
              {hasActiveFilters ? (
                <>
                  <p className="text-white/50 text-lg mb-4">No se encontraron jugadores con esos filtros</p>
                  <button
                    onClick={clearFilters}
                    className="text-white hover:text-white/80 transition-colors underline"
                  >
                    Limpiar filtros
                  </button>
                </>
              ) : (
                <>
                  <p className="text-white/50 text-lg">No hay jugadores registrados</p>
                  <Link to="/" className="inline-block mt-4 text-white hover:text-white/80 transition-colors">
                    Volver al inicio
                  </Link>
                </>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Player Preview Modal */}
      <PlayerPreviewModal 
        player={previewPlayer}
        isOpen={!!previewPlayer}
        onClose={() => setPreviewPlayer(null)}
      />
    </div>
  );
};

export default AllPlayersPage;
